import * as React from "react";
import { Button } from "@patient-watch/ui/button";
import { Input } from "@patient-watch/ui/input";
import { ClipboardCheck, Send, Star } from "lucide-react";
import Spinner from "@patient-watch/ui/spinner";

interface EmailGateDialogProps {
    open: boolean;
    onEmailSubmitted: (email: string) => Promise<void>;
    formName?: string;
    isSubmitting?: boolean;
    resultsPreview?: React.ReactNode;
}

export function EmailGateDialog({
    open,
    onEmailSubmitted,
    formName = "Form",
    isSubmitting = false,
}: EmailGateDialogProps) {
    const [email, setEmail] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);

    if (!open) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email.trim()) {
            setError("Please enter your email address");
            return;
        }
        if (!emailRegex.test(email)) {
            setError("Please enter a valid email address");
            return;
        }

        try {
            await onEmailSubmitted(email);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Something went wrong");
        }
    };

    return (
        <div className="pb-[max(2rem,env(safe-area-inset-bottom))] flex justify-center items-start mx-auto py-8 w-full min-w-0 max-w-md">
            <div className="space-y-8 px-1 sm:px-0 w-full min-w-0 max-w-lg">
                {/* Heading */}
                <div className="space-y-2 text-center">
                    <h1 className="font-bold text-slate-900 text-2xl sm:text-3xl leading-tight">
                        Your {formName} Results Are Ready
                    </h1>
                    <p className="mx-auto max-w-md text-slate-500 text-sm sm:text-base leading-relaxed">
                        Enter your email to see your scores and receive a copy
                    </p>
                </div>

                <div className="relative mx-auto rounded-[1rem] w-40 aspect-square">
                    <div className="absolute inset-0 bg-gradient-to-br from-pink-300/40 via-indigo-300/40 to-sky-300/40 shadow-lg border border-white/20 rounded-[1rem]" />
                    <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-transparent rounded-[1rem]" />
                    <div className="absolute inset-0 flex justify-center items-center">
                        {isSubmitting ? (
                            <Spinner className="w-16 h-16" />
                        ) : (
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/30 to-pink-500/30 blur-xl" />
                                <ClipboardCheck className="relative drop-shadow-lg w-16 h-16 text-indigo-600" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Input
                            id="email-gate"
                            data-testid="email-gate"
                            type="email"
                            placeholder="you@clinic.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isSubmitting}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            className="bg-white shadow-sm border-slate-200 focus:border-indigo-500 rounded-xl focus:ring-indigo-500 h-12 placeholder:text-slate-400 text-base"
                        />
                        {error && <p className="text-red-500 text-sm">{error}</p>}
                    </div>
                    <Button
                        type="submit"
                        data-testid="email-gate-submit"
                        disabled={isSubmitting}
                        size="lg"
                        className="bg-indigo-600 hover:bg-indigo-700 shadow-md px-6 rounded-xl w-full h-12 font-semibold text-white transition-colors"
                    >
                        {isSubmitting ? (
                            <>
                                <span className="mr-2 border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <Send className="mr-2 w-4 h-4" aria-hidden />
                                See my results
                            </>
                        )}
                    </Button>
                </form>

                {/* Social proof */}
                <div className="flex flex-col justify-center items-center gap-3 px-4 py-3 rounded-xl">
                    <div className="flex gap-0.5 shrink-0" aria-hidden>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className="fill-amber-400 w-4 h-4 text-amber-400" />
                        ))}
                    </div>
                    <p className="text-slate-600 text-sm">Trusted by leading MSK clinicians</p>
                </div>
            </div>
        </div>
    );
}
